'use client';

import React from 'react';
import { useTheme } from 'styled-components';

export type VehicleStatus = 'active' | 'done' | 'upcoming' | 'skipped';

export interface MapVehicleGlyphProps extends React.SVGAttributes<SVGSVGElement> {
  /** Heading in degrees (0 = north, 90 = east, 180 = south). Default 0. */
  bearing?: number;
  /** Status drives body colour. Default 'active'. */
  status?: VehicleStatus;
  /** Outer diameter in px. Default 24. */
  size?: number;
}

/**
 * Live vehicle map marker — a solid status-coloured disc with a white outer
 * ring and a white directional arrow, sized to sit on top of a schematic or
 * raster map layer. The solid fill + white foreground keeps it glanceable
 * against any basemap; the arrow rotates with `bearing`.
 *
 * @example
 * <MapVehicleGlyph bearing={45} status="active" />
 */
export function MapVehicleGlyph({
  bearing = 0,
  status = 'active',
  size = 24,
  'aria-label': ariaLabel,
  ...rest
}: MapVehicleGlyphProps) {
  const theme = useTheme();
  const statusColors = theme.colors.status[status];
  const white = theme.colors.neutral.inkOnAccent;

  const r = size / 2;
  const cx = r;
  const cy = r;
  const ringW = Math.max(2.5, size * 0.12);

  // White navigation arrow — points north at bearing 0, rotated clockwise.
  const arrow = [
    `${cx},${cy - r * 0.55}`,
    `${cx + r * 0.42},${cy + r * 0.48}`,
    `${cx},${cy + r * 0.18}`,
    `${cx - r * 0.42},${cy + r * 0.48}`,
  ].join(' ');

  const label = ariaLabel ?? `Moving marker, heading ${bearing}°, ${status}`;

  return (
    <svg
      role="img"
      aria-label={label}
      width={size + 10}
      height={size + 10}
      viewBox={`${-5} ${-5} ${size + 10} ${size + 10}`}
      {...rest}
    >
      {/* Solid disc + white ring */}
      <circle cx={cx} cy={cy} r={r} fill={statusColors.dot} stroke={white} strokeWidth={ringW} />
      {/* White heading arrow */}
      <polygon
        points={arrow}
        fill={white}
        strokeLinejoin="round"
        transform={`rotate(${bearing}, ${cx}, ${cy})`}
      />
    </svg>
  );
}
